import React, { useEffect, useState } from 'react';
import { Pencil, Trash2, Circle, ArrowRightLeft } from 'lucide-react';
import api from '../../services/api';
import { PageHeader, TableSkeleton, EmptyState, Pagination } from '../../components/PageParts';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { formatCurrency } from '../../utils/format';

const TIRE_STATUSES = [
  { v: 'in_stock', l: 'Estoque' }, { v: 'installed', l: 'Instalado' },
  { v: 'retreaded', l: 'Recapado' }, { v: 'discarded', l: 'Descartado' }, { v: 'sold', l: 'Vendido' },
];
const MOVEMENTS = [
  { v: 'install', l: 'Instalar' }, { v: 'remove', l: 'Remover' }, { v: 'rotate', l: 'Rodízio' },
  { v: 'retread', l: 'Recapar' }, { v: 'discard', l: 'Descartar' }, { v: 'sell', l: 'Vender' },
];
const POSITIONS = ['FE', 'FD', 'TE1', 'TD1', 'TE2', 'TD2', 'Estepe'];
const empty = { internal_number: '', code: '', brand_name: '', model: '', size: '', dot: '', purchase_date: '', purchase_value: '', useful_life_km: '', current_groove_mm: '', min_groove_mm: 1.6, tire_status: 'in_stock', notes: '' };
const emptyMove = { movement_type: 'install', vehicle_id: '', from_position: '', to_position: '', km: '', cost: '', notes: '' };

export default function Tires() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [statusF, setStatusF] = useState('all');
  const [alertOnly, setAlertOnly] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveTire, setMoveTire] = useState(null);
  const [moveForm, setMoveForm] = useState(emptyMove);
  const [vehicles, setVehicles] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 10 };
      if (statusF !== 'all') params.tire_status = statusF;
      if (alertOnly) params.alert = true;
      const { data } = await api.get('/tires', { params });
      setItems(data.items); setTotal(data.total);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, statusF, alertOnly]);
  useEffect(() => { (async () => { const v = await api.get('/vehicles', { params: { limit: 200 } }); setVehicles(v.data.items || []); })(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setModalOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...empty, ...t, purchase_date: t.purchase_date ? t.purchase_date.slice(0, 10) : '' }); setModalOpen(true); };
  const openMove = (tire) => { setMoveTire(tire); setMoveForm({ ...emptyMove, from_position: tire.position || '' }); setMoveOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.internal_number) { toast.error('Número interno obrigatório'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        purchase_value: form.purchase_value ? Number(form.purchase_value) : null,
        useful_life_km: form.useful_life_km ? Number(form.useful_life_km) : null,
        current_groove_mm: form.current_groove_mm ? Number(form.current_groove_mm) : null,
        min_groove_mm: form.min_groove_mm ? Number(form.min_groove_mm) : 1.6,
        purchase_date: form.purchase_date || null,
      };
      if (editing) await api.put(`/tires/${editing.id}`, payload);
      else await api.post('/tires', payload);
      toast.success(editing ? 'Pneu atualizado' : 'Pneu cadastrado');
      setModalOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); } finally { setSaving(false); }
  };

  const submitMove = async () => {
    try {
      await api.post(`/tires/${moveTire.id}/movements`, {
        ...moveForm, tire_id: moveTire.id,
        km: moveForm.km ? Number(moveForm.km) : null,
        cost: moveForm.cost ? Number(moveForm.cost) : null,
      });
      toast.success('Movimentação registrada'); setMoveOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };

  const confirmDelete = async () => { try { await api.delete(`/tires/${deleteId}`); toast.success('Excluído'); setDeleteId(null); load(); } catch { toast.error('Erro'); } };

  return (
    <div>
      <PageHeader title="Controle de Pneus" subtitle="Cadastro, movimentações e alertas de sulco" onAction={openCreate} actionLabel="Novo Pneu" />
      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4 mb-4 flex gap-3 items-center flex-wrap">
        <Select value={statusF} onValueChange={setStatusF}>
          <SelectTrigger className="w-52"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos status</SelectItem>
            {TIRE_STATUSES.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={alertOnly} onChange={e => setAlertOnly(e.target.checked)} data-testid="tires-alert-filter" />
          Apenas com alerta (abaixo do sulco mínimo)
        </label>
      </div>

      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 dark:bg-neutral-900/60 text-neutral-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3">Nº</th>
              <th className="text-left px-4 py-3">Marca / Modelo</th>
              <th className="text-left px-4 py-3">Medida / DOT</th>
              <th className="text-left px-4 py-3">Veículo / Posição</th>
              <th className="text-right px-4 py-3">Sulco</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Ações</th>
            </tr>
          </thead>
          <tbody data-testid="tires-table" className="divide-y divide-neutral-100 dark:divide-neutral-800">
            {loading ? <tr><td colSpan={7} className="p-4"><TableSkeleton rows={5} cols={7} /></td></tr> :
              items.length === 0 ? <tr><td colSpan={7}><EmptyState icon={Circle} title="Nenhum pneu cadastrado" /></td></tr> :
              items.map(t => (
                <tr key={t.id} className={`tr-hover ${t.alerts?.groove_below_min ? 'bg-red-50/50 dark:bg-red-950/10' : ''}`}>
                  <td className="px-4 py-3 font-mono text-xs">{t.internal_number}</td>
                  <td className="px-4 py-3"><div className="text-xs">{t.brand_name || '-'}</div><div className="text-xs text-neutral-500">{t.model || '-'}</div></td>
                  <td className="px-4 py-3 text-xs">{t.size || '-'} <span className="text-neutral-400">/ {t.dot || '-'}</span></td>
                  <td className="px-4 py-3 text-xs">{t.vehicle_plate || '-'} <span className="text-neutral-400">{t.position ? `(${t.position})` : ''}</span></td>
                  <td className="px-4 py-3 text-right text-xs">
                    <span className={t.alerts?.groove_below_min ? 'text-red-600 font-bold' : t.alerts?.groove_warn ? 'text-amber-600' : ''}>
                      {t.current_groove_mm || '-'}mm
                    </span>
                    <span className="text-neutral-400"> / min {t.min_groove_mm || 1.6}mm</span>
                  </td>
                  <td className="px-4 py-3 text-xs">{TIRE_STATUSES.find(s => s.v === t.tire_status)?.l || t.tire_status}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Button data-testid={`move-tire-${t.internal_number}`} size="icon" variant="ghost" title="Movimentar" onClick={() => openMove(t)}><ArrowRightLeft className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => openEdit(t)}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="text-red-600" onClick={() => setDeleteId(t.id)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        <Pagination page={page} total={total} limit={10} onPageChange={setPage} />
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? 'Editar' : 'Novo'} pneu</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Nº interno *</Label><Input required value={form.internal_number} onChange={e => setForm(f => ({ ...f, internal_number: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Código</Label><Input value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Marca</Label><Input value={form.brand_name} onChange={e => setForm(f => ({ ...f, brand_name: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Modelo</Label><Input value={form.model} onChange={e => setForm(f => ({ ...f, model: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Medida</Label><Input value={form.size} onChange={e => setForm(f => ({ ...f, size: e.target.value }))} placeholder="295/80R22.5" /></div>
            <div className="space-y-1.5"><Label>DOT</Label><Input value={form.dot} onChange={e => setForm(f => ({ ...f, dot: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Data compra</Label><Input type="date" value={form.purchase_date} onChange={e => setForm(f => ({ ...f, purchase_date: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Valor</Label><Input type="number" step="0.01" value={form.purchase_value} onChange={e => setForm(f => ({ ...f, purchase_value: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Vida útil (km)</Label><Input type="number" value={form.useful_life_km} onChange={e => setForm(f => ({ ...f, useful_life_km: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Sulco atual (mm)</Label><Input type="number" step="0.1" value={form.current_groove_mm} onChange={e => setForm(f => ({ ...f, current_groove_mm: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Sulco mínimo (mm)</Label><Input type="number" step="0.1" value={form.min_groove_mm} onChange={e => setForm(f => ({ ...f, min_groove_mm: e.target.value }))} /></div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.tire_status} onValueChange={v => setForm(f => ({ ...f, tire_status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TIRE_STATUSES.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 md:col-span-2"><Label>Observações</Label><Textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
            <DialogFooter className="md:col-span-2">
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-brand hover:bg-[#A81E23] text-white">{saving ? 'Salvando...' : 'Salvar'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Movimentar pneu {moveTire?.internal_number}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5 md:col-span-2">
              <Label>Tipo</Label>
              <Select value={moveForm.movement_type} onValueChange={v => setMoveForm(f => ({ ...f, movement_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{MOVEMENTS.map(m => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {['install', 'rotate'].includes(moveForm.movement_type) && (
              <>
                <div className="space-y-1.5 md:col-span-2">
                  <Label>Veículo</Label>
                  <Select value={moveForm.vehicle_id || 'none'} onValueChange={v => setMoveForm(f => ({ ...f, vehicle_id: v === 'none' ? '' : v }))}>
                    <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.plate}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {moveForm.movement_type === 'rotate' && (
                  <div className="space-y-1.5"><Label>De</Label>
                    <Select value={moveForm.from_position || 'none'} onValueChange={v => setMoveForm(f => ({ ...f, from_position: v === 'none' ? '' : v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="none">—</SelectItem>{POSITIONS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-1.5"><Label>Posição</Label>
                  <Select value={moveForm.to_position || 'none'} onValueChange={v => setMoveForm(f => ({ ...f, to_position: v === 'none' ? '' : v }))}>
                    <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">—</SelectItem>{POSITIONS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </>
            )}
            <div className="space-y-1.5"><Label>KM do veículo</Label><Input type="number" value={moveForm.km} onChange={e => setMoveForm(f => ({ ...f, km: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Custo</Label><Input type="number" step="0.01" value={moveForm.cost} onChange={e => setMoveForm(f => ({ ...f, cost: e.target.value }))} /></div>
            <div className="space-y-1.5 md:col-span-2"><Label>Observações</Label><Textarea rows={2} value={moveForm.notes} onChange={e => setMoveForm(f => ({ ...f, notes: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveOpen(false)}>Cancelar</Button>
            <Button data-testid="save-tire-move" onClick={submitMove} className="bg-brand hover:bg-[#A81E23] text-white">Registrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={o => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Excluir pneu?</AlertDialogTitle></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
